import { Form, SelectField, SubmitButton } from '@/components';
import {
  ReportInvestmentFundsFilterSchema,
  type reportFileType,
} from '@/features/mold/models';
import { useGenerateInvestmentFundsReport } from '@/features/project/_hooks/report';
import type { useFiscalYearOptions } from '@/features/search/_hooks/budget-types';
import { useOrganizations } from '@/features/tenant-manager/hooks';
import { useForm } from '@/hooks';
import { useMessage } from '@/providers';
import { currentFiscalYear } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof ReportInvestmentFundsFilterSchema>;
type InitialFormValues = Pick<FormValues, 'fiscalYear'> &
  Partial<Omit<FormValues, 'fiscalYear'>>;

type Props = {
  fileType: z.infer<typeof reportFileType>;
  fiscalYearOptions: ReturnType<typeof useFiscalYearOptions>;
};

export const ReportInvestmentFundForm: FC<Props> = ({
  fileType,
  fiscalYearOptions,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting, isValid, isDirty, isSubmitSuccessful },
  } = useForm<InitialFormValues, FormValues>({
    defaultValues: {
      fiscalYear: Number(currentFiscalYear()),
    } satisfies InitialFormValues,
    resolver: zodResolver(ReportInvestmentFundsFilterSchema),
  });
  const { openMessage } = useMessage();

  const selectedOrganizationIsAll = watch('organizationIds')?.includes('all');
  const fiscalYear = watch('fiscalYear');

  const { data: organizationOptions, isLoading: isOrganizationOptionsLoading } =
    useOrganizations({
      params: {
        level: 1,
        showMigrated: true,
        referenceFiscalYear: fiscalYear.toString(),
      },
      select: (data) =>
        data.map((item) => {
          return {
            label: item.name,
            value: item.id,
            disabled: selectedOrganizationIsAll,
          };
        }),
      enabled: !!fiscalYear,
    });

  const { mutateAsync } = useGenerateInvestmentFundsReport();

  const onSubmit = async (values: FormValues) => {
    const isAllOrganization = values.organizationIds.includes('all');
    const paramsOrganizationIds = isAllOrganization
      ? []
      : values.organizationIds;

    try {
      await mutateAsync({
        ...values,
        organizationIds: paramsOrganizationIds,
        fileType,
        isAllOrganization,
      });
      await openMessage(
        'success',
        t('export-file-requested', {
          target: t('report-type-investment-fund'),
        }),
      );
    } catch {
      await openMessage(
        'error',
        t('error-message-file-export-failed', {
          fileName: t('report-type-investment-fund'),
        }),
      );
    }
  };

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
      style={{ maxWidth: 480 }}
    >
      <SelectField
        name="fiscalYear"
        label={t('fiscal-year')}
        control={control}
        options={fiscalYearOptions.data}
        errors={errors}
        allowClear={false}
        required
      />
      <SelectField
        name="organizationIds"
        label={t('organization-in-charge')}
        options={[
          {
            label: t('all-organizations'),
            value: 'all',
          },
          ...(organizationOptions ?? []),
        ]}
        onChange={(value) => {
          setValue('organizationIds', value.includes('all') ? ['all'] : value);
        }}
        shouldCoexistOnChange
        mode="multiple"
        control={control}
        errors={errors}
        allowClear={false}
        required
        loading={isOrganizationOptionsLoading}
      />

      <SubmitButton
        text={t('export')}
        loading={isSubmitting}
        disabled={!isValid}
      />
    </Form>
  );
};
